/**
 * useMutation hooks for the PDF styles write path.
 *
 * Uses `useGraphQLMutation` from @opengov/bnp-core for Hasura, writes to
 * the TanStack Query cache via `setQueryData` with the narrow
 * `publicationSetupPdfQueryKeys.styles(params)` key.
 *
 * `isPending` from each hook replaces the compose `isApplyingStyles` state.
 *
 * Note: `exportPdfSettings` is not in the @opengov/bnp-stories-hasura-service-schema
 * package so we use raw string documents rather than codegen graphql() tagged templates.
 * The `useGraphQLMutation` hook accepts `document: TypedDocumentNode | string`.
 *
 * The `getUserById` lookup (previously inside this file) has been moved to
 * the call site in `PublicationSetupPDFView.tsx` so this hook receives a
 * fully-resolved `object` from the caller.
 */

import { useGraphQLMutation } from '@opengov/bnp-core';
// `useQueryClient` is not exported by @opengov/bnp-core — imported directly from TanStack.
import { useQueryClient } from '@tanstack/react-query';
import { IPublicationSetupPdfStyle } from '@opengov/stories-shared/constants';
import { useToast } from '../../../../components/Toast/context/useToast.context';

import { HASURA_SERVICE_URL } from '../../../../../config/config';
import { publicationSetupPdfQueryKeys, PublicationSetupPdfStylesKeyParams } from './publicationSetupPdfQueryKeys';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

/** Server shape returned by the upsert mutation — uses exportType/exportId, not publicationId/sectionId. */
export type IUpsertResponseResource = {
  id: string;
  exportType: 'publication' | 'section';
  exportId: string;
  coverStoryId: string | null;
  pageMargins: IPublicationSetupPdfStyle['pageMargins'];
  headerControls: IPublicationSetupPdfStyle['headerControls'];
  footerControls: IPublicationSetupPdfStyle['footerControls'];
  tocStyles: string;
  tocData: string | null;
  watermark: string | null;
  storyStates: string | null;
};

type IUpsertResponse = { resource: IUpsertResponseResource };

type IDeleteResponse = {
  delete_exportPdfSettings: {
    affected_rows: number;
    returning: { exportId: string; id: string }[];
  };
};

/** Upsert object accepted by `useUpsertPdfStyles`.
 *  The caller is responsible for building the full insert object including
 *  `createdBy` (resolved via `getUserById`), `exportType`, `exportId`, and
 *  JSON-stringified fields. */
export type UpsertPdfStylesObject = {
  createdBy: string;
  exportType: 'publication' | 'section';
  exportId: string;
  coverStoryId?: string | null;
  pageMargins?: IPublicationSetupPdfStyle['pageMargins'];
  headerControls?: IPublicationSetupPdfStyle['headerControls'];
  footerControls?: IPublicationSetupPdfStyle['footerControls'];
  tocStyles?: string;
  tocData?: string;
  watermark?: string | null;
  storyStates?: string | null;
};

// ---------------------------------------------------------------------------
// GraphQL document strings
// Note: raw strings because exportPdfSettings is not in the codegen schema.
// ---------------------------------------------------------------------------

const UPSERT_PDF_STYLES_MUTATION = `
  mutation UpsertSetupPdfStylesForSetupPdf($object: exportPdfSettings_insert_input!) {
    resource: insert_exportPdfSettings_one(
      object: $object
      on_conflict: {
        constraint: exportPdfSettings_exportType_exportId_key
        update_columns: [coverStoryId, pageMargins, headerControls, footerControls, tocStyles, tocData, watermark, storyStates]
      }
    ) {
      id
      exportType
      exportId
      coverStoryId
      pageMargins
      headerControls
      footerControls
      tocStyles
      tocData
      watermark
      storyStates
    }
  }
`;

const DELETE_PDF_STYLES_MUTATION = `
  mutation DeleteExportPdfSettingsForSetupPdf($exportId: uuid!) {
    delete_exportPdfSettings(where: { exportId: { _eq: $exportId } }) {
      affected_rows
      returning {
        exportId
        id
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useUpsertPdfStyles(params: PublicationSetupPdfStylesKeyParams, errorMessage: string) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();
  return useGraphQLMutation<IUpsertResponse, { object: UpsertPdfStylesObject }>({
    mutationKey: publicationSetupPdfQueryKeys.upsertStyles(params),
    document: UPSERT_PDF_STYLES_MUTATION,
    url: HASURA_SERVICE_URL,
    options: {
      onSuccess: (data) => {
        // setQueryData must write the raw server shape that usePublicationSetupPdfStyles's
        // select() expects — not the normalized IPublicationSetupPdfStyle — otherwise
        // select() receives data.publicationSetupPdfStyles = undefined and returns null,
        // wiping out the just-saved styles until the next page refresh.
        queryClient.setQueryData(publicationSetupPdfQueryKeys.styles(params), {
          publicationSetupPdfStyles: [data.resource]
        });
      },
      onError: () => {
        toggleToast({ title: errorMessage, severity: 'error' });
      }
    }
  });
}

export function useDeletePdfStyles(params: PublicationSetupPdfStylesKeyParams, errorMessage: string) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();
  return useGraphQLMutation<IDeleteResponse, { exportId: string }>({
    mutationKey: publicationSetupPdfQueryKeys.deleteStyles(params),
    document: DELETE_PDF_STYLES_MUTATION,
    url: HASURA_SERVICE_URL,
    options: {
      onSuccess: () => {
        queryClient.setQueryData(publicationSetupPdfQueryKeys.styles(params), null);
      },
      onError: () => {
        toggleToast({ title: errorMessage, severity: 'error' });
      }
    }
  });
}
